// Политика проверки связи реальным запросом (проба /api/ping).
//
// Проблема: navigator.onLine и события online/offline на Android/TWA
// отслеживают сетевой интерфейс, а не реальную связь, и после возврата
// сети событие часто не приходит — флаг «залипает» в офлайне. Из-за этого
// баннер «Нет подключения» висел вечно, а flush() офлайн-очереди (гейт по
// isOnline()) не отправлял изменения до ручной перезагрузки страницы.
//
// Решение: браузерному флагу верим только в сторону «офлайн» (интерфейс
// упал — связи точно нет). Состояние «онлайн» подтверждаем реальной пробой:
// пока считаем себя офлайн — периодически дёргаем /api/ping; ЛЮБОЙ
// HTTP-ответ (даже 4xx/5xx) означает «сеть есть» — до сервера долетели.
// Ошибка fetch (TypeError/timeout) — связи нет.
//
// Pure-модуль: решения тестируются в vitest, браузерную обвязку (fetch,
// таймеры, события) держит web/lib/networkStatus.ts и vanilla офлайн-экраны.

/** Интерпретация исхода пробы: любой HTTP-ответ = связь есть. */
export function probeIndicatesOnline(result: { responded: boolean }): boolean {
  return result.responded === true;
}

/**
 * Пауза перед следующей пробой (мс) по числу неудачных попыток подряд.
 * Первая перепроверка быстрая (баннер должен сниматься шустро), дальше
 * бэкофф до потолка — чтобы не жечь батарею на долгом офлайне.
 * 0 → 3с, 1 → 10с, дальше 30с.
 */
export function nextProbeDelayMs(failedAttempts: number): number {
  if (failedAttempts <= 0) return 3_000;
  if (failedAttempts === 1) return 10_000;
  return 30_000;
}

/** Таймаут одной пробы (мс) — дольше ждать нет смысла, следующая догонит. */
export const PROBE_TIMEOUT_MS = 5_000;

/**
 * Итоговое состояние сети из двух источников.
 * - Флаг браузера false → офлайн сразу (не ждём пробу): интерфейс упал.
 * - Флаг true → верим последней пробе, если она была; иначе флагу.
 *   (probeOnline === null — проба ещё не выполнялась.)
 */
export function effectiveOnline(
  navigatorOnline: boolean,
  probeOnline: boolean | null,
): boolean {
  if (!navigatorOnline) return false;
  return probeOnline === null ? navigatorOnline : probeOnline;
}

/**
 * Нужно ли сейчас крутить цикл проб. Пробуем, пока itogovoe состояние —
 * офлайн (независимо от того, кто его показал: флаг или проба) — именно
 * проба вернёт нас в онлайн, когда флаг «залип».
 */
export function shouldKeepProbing(
  navigatorOnline: boolean,
  probeOnline: boolean | null,
): boolean {
  return !effectiveOnline(navigatorOnline, probeOnline);
}
